import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import layoutStyles from "../layout/SectionLayout.module.css";
import styles from "./GroupSortControl.module.css";
import { groupSortKeys, type GroupSortKey } from "./groupSort";
import { Tooltip } from "../shared/Tooltip";

/**
 * The My Groups sort choice: a custom dropdown in the page's control group beside the layout buttons.
 * The trigger shows the order in use with its icon; the menu lists each order with its icon. It works
 * with the mouse, and from the keyboard: Enter or Space opens it, the arrow keys move, Enter chooses,
 * and Escape closes it.
 *
 * @param props.value - The order currently in use.
 * @param props.onChange - Called with the newly chosen order.
 */
export function GroupSortControl({
  value,
  onChange,
  projectedAvailable,
}: {
  value: GroupSortKey;
  onChange: (sortKey: GroupSortKey) => void;
  /** Whether projected spend exists for the period on screen: it is only projected for the current month. */
  projectedAvailable: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const idBase = useId();
  const isAvailable = (key: GroupSortKey) => key !== "projected" || projectedAvailable;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() => groupSortKeys.indexOf(value));

  // Focus moves to the menu when it opens, so the arrow keys work straight away.
  useEffect(() => {
    if (open) {
      menuRef.current?.focus();
    }
  }, [open]);

  // A click anywhere outside the control closes the menu.
  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  const openMenu = () => {
    setActiveIndex(groupSortKeys.indexOf(value));
    setOpen(true);
  };

  const closeMenu = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  const choose = (sortKey: GroupSortKey) => {
    if (!isAvailable(sortKey)) {
      return;
    }

    onChange(sortKey);
    closeMenu(true);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % groupSortKeys.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + groupSortKeys.length) % groupSortKeys.length);
        break;
      case "Home":
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        setActiveIndex(groupSortKeys.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(groupSortKeys[activeIndex]);
        break;
      case "Escape":
        event.preventDefault();
        closeMenu(true);
        break;
      case "Tab":
        closeMenu(false);
        break;
    }
  };

  const menuId = `${idBase}-menu`;

  return (
    <div className={styles.dropdown} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.triggerLabel}>{t("myGroups.sort.label")}</span>
        <SortIcon sortKey={value} />
        <span>{t(`myGroups.sort.${value}`)}</span>
        <svg className={styles.chevron} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          ref={menuRef}
          id={menuId}
          role="listbox"
          tabIndex={-1}
          aria-label={t("myGroups.sort.label")}
          aria-activedescendant={`${idBase}-option-${activeIndex}`}
          className={styles.menu}
          onKeyDown={onMenuKeyDown}
        >
          {groupSortKeys.map((key, index) => (
            <li
              key={key}
              id={`${idBase}-option-${index}`}
              role="option"
              aria-selected={key === value}
              aria-disabled={!isAvailable(key)}
              className={`${styles.option} ${index === activeIndex ? styles.optionActive : ""} ${isAvailable(key) ? "" : styles.optionDisabled}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(key)}
            >
              <Tooltip label={isAvailable(key) ? undefined : t("myGroups.sort.projectedUnavailable")}>
                <SortIcon sortKey={key} />
                <span>{t(`myGroups.sort.${key}`)}</span>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A small line icon for each order. */
function SortIcon({ sortKey }: { sortKey: GroupSortKey }): React.JSX.Element {
  return (
    <svg className={layoutStyles.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      {iconPaths[sortKey]}
    </svg>
  );
}

const iconPaths: Record<GroupSortKey, React.JSX.Element> = {
  // A branching tree: divisions, then what sits beneath them.
  hierarchy: <path d="M3 2h4v3H3zM9 11h4v3H9zM3 5v8h6M3 9h6" fill="none" stroke="currentColor" strokeWidth={1.5} />,
  // Lines shortening from A to Z.
  name: <path d="M2 4h12M2 8h8M2 12h4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />,
  // A dollar sign, for spend.
  spend: <path d="M11 5.5C10.5 4.6 9.5 4 8 4 6.2 4 5 4.9 5 6.2 5 9.6 11 8.6 11 11.8 11 13.1 9.8 14 8 14c-1.5 0-2.6-.6-3-1.5M8 2v2M8 13v1" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />,
  // Two people, one behind the other, for head count.
  people: <path d="M6 3.5a2.2 2.2 0 110 4.4 2.2 2.2 0 010-4.4zM1.5 13.5c0-2.2 1.9-3.8 4.5-3.8s4.5 1.6 4.5 3.8M11.2 3.4a2.1 2.1 0 010 4.2M11.5 9.9c1.9.3 3.2 1.8 3.2 3.6" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />,
  // A single person, for spend per person.
  perPerson: <path d="M8 2.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM3 14c0-3 2.2-5 5-5s5 2 5 5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />,
  // A rising trend line, for projection.
  projected: <path d="M2 12l4-4 3 3 5-6M10 5h4v4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />,
};
